import React, { useMemo } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { theme } from '../../styles/constants';
import OrganizationAnalyticsChartCard from './OrganizationAnalyticsChartCard';
import { CHART_COLORS, formatValueByType, getDefaultTooltipFormatter } from './chartUtils';

const OrganizationAnalyticsBarChart = ({
  title,
  subtitle,
  categories,
  series,
  pending,
  emptyText,
  valueType = 'currency',
  stacked = false,
  tooltipFormatter,
}) => {
  const chartSeries = useMemo(() => series.filter(item => item.data?.length), [series]);
  const hasData = categories.length > 0 && chartSeries.length > 0;
  const visibleCategoryCount = Math.max(1, Math.min(categories.length, 10));
  const chartHeight = Math.max(220, visibleCategoryCount * 32 + 120);
  const fullChartHeight = Math.max(220, Math.max(categories.length, 1) * 32 + 120);

  const options = useMemo(
    () => ({
      chart: {
        type: 'bar',
        backgroundColor: '#fff',
        height: chartHeight,
        scrollablePlotArea:
          categories.length > 10
            ? {
                minHeight: fullChartHeight,
                opacity: 1,
              }
            : undefined,
        style: {
          fontFamily: 'Lexend',
        },
      },
      title: {
        text: '',
      },
      accessibility: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      legend: {
        itemStyle: {
          color: theme.colors.textColor,
          fontSize: 12,
        },
      },
      xAxis: {
        categories,
        labels: {
          style: {
            color: theme.colors.textColor,
            fontSize: 11,
          },
        },
        gridLineColor: '#F3F4F6',
        gridLineDashStyle: 'Dot',
        gridLineWidth: 1,
      },
      yAxis: {
        title: {
          text: '',
        },
        gridLineColor: '#F3F4F6',
        gridLineDashStyle: 'Dot',
        labels: {
          formatter: function () {
            return formatValueByType(this.value, valueType);
          },
          style: {
            color: theme.colors.textColor,
            fontSize: 12,
          },
        },
      },
      plotOptions: {
        series: {
          stacking: stacked ? 'normal' : undefined,
          dataLabels: {
            enabled: false,
          },
          borderWidth: 0,
          borderRadius: 5,
          groupPadding: 0.08,
          pointWidth: 20,
        },
      },
      colors: CHART_COLORS,
      tooltip: {
        shared: true,
        style: {
          fontSize: 12,
        },
        formatter: tooltipFormatter || getDefaultTooltipFormatter(valueType),
      },
      series: chartSeries,
    }),
    [categories, chartHeight, chartSeries, fullChartHeight, stacked, tooltipFormatter, valueType],
  );

  return (
    <OrganizationAnalyticsChartCard
      title={title}
      subtitle={subtitle}
      pending={pending}
      hasData={hasData}
      emptyText={emptyText}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </OrganizationAnalyticsChartCard>
  );
};

export default OrganizationAnalyticsBarChart;
